import React from "react"
import { Link, useLocation } from "react-router-dom"
import { Box, Flex, Text, Separator, IconButton, ScrollArea } from "@radix-ui/themes"
import { LucideIcon, LogOut, X, ChevronDown, ChevronRight } from "lucide-react"
import TurboLogo from "../../TurboLogo"
import * as Collapsible from "@radix-ui/react-collapsible"

export interface MenuItem {
    path: string
    icon: LucideIcon | React.FC<{ className?: string; size?: number }>
    label: string
    badge?: number
}

export interface MenuGroup {
    title: string
    items: MenuItem[]
}

interface AdminSidebarProps {
    groups: MenuGroup[]
    title: string
    subtitle: string
    onLogout?: () => void
    onClose?: () => void
    logoColor?: string
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({
    groups,
    title,
    subtitle,
    onLogout,
    onClose,
    logoColor = "#DC2626"
}) => {
    const location = useLocation()

    // State to track open groups - default all open
    const [openGroups, setOpenGroups] = React.useState<string[]>(groups.map(g => g.title))

    const toggleGroup = (groupTitle: string) => {
        setOpenGroups(prev =>
            prev.includes(groupTitle)
                ? prev.filter(t => t !== groupTitle)
                : [...prev, groupTitle]
        )
    }

    const handleNavClick = () => {
        if (onClose) {
            onClose()
        }
    }

    return (
        <Box
            className="w-64 min-h-screen flex flex-col"
            style={{
                backgroundColor: 'var(--color-background)',
                boxShadow: 'var(--shadow-4)',
                borderRight: '1px solid var(--gray-4)'
            }}
        >
            <Box p="5">
                <Flex align="center" justify="between">
                    <Flex align="center" gap="3" style={{ direction: 'ltr' }}>
                        <TurboLogo width={36} height={30} color={logoColor} />
                        <Flex direction="column" gap="0">
                            <Text size="3" weight="bold" style={{ lineHeight: 1.2 }}>{title}</Text>
                            <Text size="1" color="gray" style={{ lineHeight: 1.2 }}>{subtitle}</Text>
                        </Flex>
                    </Flex>

                    {onClose && (
                        <IconButton
                            variant="ghost"
                            size="2"
                            onClick={onClose}
                            className="lg:hidden"
                        >
                            <X className="w-5 h-5" />
                        </IconButton>
                    )}
                </Flex>
            </Box>

            <Separator size="4" />

            <ScrollArea type="hover" scrollbars="vertical" style={{ flex: 1 }}>
                <Box p="3">
                    <Flex direction="column" gap="4">
                        {groups.map((group) => (
                            <Collapsible.Root
                                key={group.title}
                                open={openGroups.includes(group.title)}
                                onOpenChange={() => toggleGroup(group.title)}
                            >
                                <Collapsible.Trigger asChild>
                                    <Flex
                                        align="center"
                                        justify="between"
                                        className="group cursor-pointer hover:bg-[var(--gray-3)] p-2 rounded-md transition-colors mb-1"
                                    >
                                        <Text size="1" weight="bold" color="gray" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            {group.title}
                                        </Text>
                                        {openGroups.includes(group.title) ? (
                                            <ChevronDown className="w-3 h-3 text-gray-400 group-hover:text-gray-600" />
                                        ) : (
                                            <ChevronRight className="w-3 h-3 text-gray-400 group-hover:text-gray-600" />
                                        )}
                                    </Flex>
                                </Collapsible.Trigger>

                                <Collapsible.Content>
                                    <Flex direction="column" gap="1" className="pl-1">
                                        {group.items.map((item) => {
                                            const Icon = item.icon
                                            const isActive = location.pathname === item.path
                                            // @ts-ignore
                                            const badgeCount = item.badge

                                            return (
                                                <Link
                                                    key={item.path}
                                                    to={item.path}
                                                    onClick={handleNavClick}
                                                    className={`
                            flex items-center px-3 py-2 rounded-md text-sm transition-all duration-200 group relative
                            ${isActive
                                                            ? 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400 font-medium'
                                                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200'
                                                        }
                          `}
                                                >
                                                    <Icon className={`w-4 h-4 mr-3 ${isActive ? 'text-red-600 dark:text-red-400' : 'text-gray-500'}`} />
                                                    <span>{item.label}</span>

                                                    {badgeCount && badgeCount > 0 ? (
                                                        <span className="absolute right-2 px-1.5 py-0.5 min-w-[18px] text-center rounded-full bg-red-600 text-white text-[10px] font-bold shadow-sm">
                                                            {badgeCount > 99 ? '99+' : badgeCount}
                                                        </span>
                                                    ) : null}

                                                    {isActive && (
                                                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-red-600 rounded-r-full" />
                                                    )}
                                                </Link>
                                            )
                                        })}
                                    </Flex>
                                </Collapsible.Content>
                            </Collapsible.Root>
                        ))}
                    </Flex>
                </Box>
            </ScrollArea>

            <Separator size="4" />

            {onLogout && (
                <Box p="4">
                    <button
                        onClick={onLogout}
                        className="flex items-center w-full px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400 rounded-md transition-colors"
                    >
                        <LogOut className="w-4 h-4 mr-3" />
                        <span>Logout</span>
                    </button>
                </Box>
            )}
        </Box>
    )
}

export default AdminSidebar
